import { CameraSubject, CameraSubjectResponseStatus } from 'streem-sdk-protobuf';
import { StreemError } from '../../streem_error';
import { Subject } from 'rxjs';
import { scan, map, shareReplay, filter, distinctUntilKeyChanged } from 'rxjs/operators';
import { logger } from '../../util/logger';
import { invariant } from '@streem/toolbox';
import { filterEmptyArray } from '../../util/rx/filter_empty_array';
import { firstFromGroup } from '../../util/rx/first_from_group';
import { findInGroup, accumulateAndMapWallEvents, mapWallItemFromWallItemEvents, } from '../wall/utils';
import { lastCreatedAt } from '../../util/rx/last_created_at';
import { recordCameraSubjectChanged, recordCameraSubjectChangeRejected, recordCameraSubjectChangeRequested, recordCameraSubjectChangeAborted, } from '@streem/analytics';
const ANALYTICS_CAMERA_SUBJECT_MAP = {
    [CameraSubject.CAMERA_SUBJECT_PERSON]: 'PERSON',
    [CameraSubject.CAMERA_SUBJECT_SCREEN]: 'SCREEN',
    [CameraSubject.CAMERA_SUBJECT_WORLD]: 'WORLD',
};
export class CameraController {
    constructor(config) {
        this.config = config;
        this.log = logger.extend('sdk:cam_controller');
        this.destroyed = new Subject();
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when initializing CameraController');
        invariant(this.config.context.remoteUserId, 'Expected remoteUserId to be defined when initializing CameraController');
        this.requests = this.config.roomWall.itemsByType.pipe(findInGroup('cameraSubjectRequest'), accumulateAndMapWallEvents('cameraSubjectRequest'), this.requestedOf(this.config.context.localUserId), filterEmptyArray(), lastCreatedAt(), scan((all, current) => [current, ...all], []));
        this.responses = this.config.roomWall.itemsByType.pipe(findInGroup('cameraSubjectResponse'), accumulateAndMapWallEvents('cameraSubjectResponse'), filterEmptyArray(), lastCreatedAt(), distinctUntilKeyChanged('inResponseToWallItemId'), scan((all, current) => [current, ...all], []), shareReplay(1));
        // Removes the pipe operator filtering  by local user ID so the latest request is always returned from any user request
        this.latestRequest = this.config.roomWall.itemsByType.pipe(findInGroup('cameraSubjectRequest'), accumulateAndMapWallEvents('cameraSubjectRequest'), filterEmptyArray(), lastCreatedAt(), scan((all, current) => [current, ...all], []), firstFromGroup());
        this.latestResponse = this.responses.pipe(filterEmptyArray(), firstFromGroup());
        // When exiting screenshare, we want to return the user to the previous camera subject used before entering screenshare
        // This property filters the video track wall items to return the most recent camera subject used by the remote participant that is not screenshare
        this.latestRemoteCameraSubjectThatIsNotScreenshare = this.config.roomWall.itemsByType.pipe(findInGroup('videoTrack'), mapWallItemFromWallItemEvents('videoTrack'), filter(track => track !== undefined &&
            track.cameraSubject !== CameraSubject.CAMERA_SUBJECT_SCREEN &&
            track.ownerUserId === this.config.context.remoteUserId?.toString()), map(track => track?.cameraSubject || CameraSubject.CAMERA_SUBJECT_WORLD), shareReplay(1));
    }
    disconnect() {
        this.destroyed.next(undefined);
    }
    /**
     * Adds a camera flip (camera subject request) to the room wall
     */
    requestCameraSubjectChange(cameraSubject) {
        const { remoteUserId, remoteDeviceId } = this.config.context;
        if (!remoteUserId || !remoteDeviceId) {
            const msg = 'remoteUserId and remoteDeviceId are required for cameraSubject request';
            this.log.error(msg);
            throw new StreemError(msg);
        }
        const requestId = this.config.roomWall.addCameraSubjectRequest(remoteUserId, remoteDeviceId, cameraSubject);
        recordCameraSubjectChangeRequested(this.config.context.roomId, ANALYTICS_CAMERA_SUBJECT_MAP[cameraSubject]);
        this.log.info(`Camera Flip requested by ${remoteUserId}`, requestId);
        return requestId;
    }
    fulfillCameraSubjectChangeRequest(inResponseToWallItemId, cameraSubject) {
        // Let the Expert know the request was accepted
        const requestId = this.config.roomWall.addCameraSubjectResponse(inResponseToWallItemId, true, CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ACCEPTED);
        recordCameraSubjectChanged(this.config.context.roomId, ANALYTICS_CAMERA_SUBJECT_MAP[cameraSubject]);
        this.log.info(`Camera Flip fulfilled by ${this.config.context.localUserId} with CameraSubject ${cameraSubject}.`, requestId);
        return requestId;
    }
    rejectCameraSubjectChangeRequest(inResponseToWallItemId) {
        const responseId = this.config.roomWall.addCameraSubjectResponse(inResponseToWallItemId, false, CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_REJECTED);
        recordCameraSubjectChangeRejected(this.config.context.roomId);
        this.log.info(`Camera Flip rejected by ${this.config.context.localUserId}`);
        return responseId;
    }
    abortCameraSubjectChangeRequest(inResponseToWallItemId) {
        const responseId = this.config.roomWall.addCameraSubjectResponse(inResponseToWallItemId, false, CameraSubjectResponseStatus.CAMERA_SUBJECT_RESPONSE_STATUS_ABORTED);
        recordCameraSubjectChangeAborted(this.config.context.roomId);
        this.log.info(`Camera Flip aborted by ${this.config.context.localUserId}`);
        return responseId;
    }
    /**
     * Filters camera subject requests by who is requested to **provide** the camera subject
     * Elsewhere, we use `requestedOf` in the request object, but for CameraSubjectRequests
     * we use `requestedFor`. For that reason, we have our own private method here to filter.
     * @param userId the user who should provide the cameraSubject
     */
    requestedOf(userId) {
        return map((requests) => requests.filter(request => request.requestedForUserId === userId.toString()));
    }
}
//# sourceMappingURL=camera_controller.js.map